import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { GenericCoordinates } from '../../location.types'
import { RouteDistanceRequest } from '../../../route-distance/route-distance.types'
import { delayedRetry } from '../../../../shared/utils/delayedRetryOperator'
import { DrrHttpClient } from '../../../../http/drr-http-client'
import { AwsDirectionsRequest, AwsDirectionsResponse, LocationDistanceService } from '../location.interface'

@Injectable({ providedIn: 'root' })
export class AwsDirectionsService implements LocationDistanceService {

  retryDelay = 1000
  retries = 2

  constructor(private http: DrrHttpClient) {}

  _calculateDistance(origin: GenericCoordinates, destination: GenericCoordinates) {
    if (!origin.latitude || !origin.longitude
      || !destination.latitude || !destination.longitude) {
      return of(NaN)
    }

    const requestBody: AwsDirectionsRequest = {
      start: {
        latitude: parseFloat(origin.latitude.toString()),
        longitude: parseFloat(origin.longitude.toString())
      },
      end: {
        latitude: parseFloat(destination.latitude.toString()),
        longitude: parseFloat(destination.longitude.toString())
      }
    }

    return this.http.post<AwsDirectionsResponse>('bff/roadservice/geo/calculate-route', requestBody).pipe(
      switchMap((response: AwsDirectionsResponse) => {
        // Convert distance from meters to miles
        // Distance is now in meters, so we convert: meters * 0.000621371 = miles
        if (!response.Routes || response.Routes.length === 0) {
          throw new Error('Invalid response structure')
        }
        
        // Find the shortest distance among all available routes
        const distances = response.Routes
          .filter(route => route.Summary && route.Summary.Distance !== undefined && route.Summary.Distance !== null)
          .map(route => route.Summary.Distance)
        
        if (distances.length === 0) {
          throw new Error('No valid routes found')
        }
        
        const shortestDistanceMeters = Math.min(...distances)
        const distanceInMiles = shortestDistanceMeters * 0.000621371
        return of(distanceInMiles)
      })
    )
  }

  calculateDistance = (routeDistanceRequest: RouteDistanceRequest): Observable<number> =>
    of(routeDistanceRequest)
      .pipe(
        switchMap((_routeDistanceRequest) =>
          this._calculateDistance(_routeDistanceRequest.origin, _routeDistanceRequest.destination)),
        delayedRetry(this.retryDelay, this.retries)
      )

}
